import React from "react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, description, actions, children }) {
  return (
    <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-foreground sm:text-3xl">{title}</h1>
        {description && <p className="mt-1 text-base text-muted-foreground">{description}</p>}
        {children}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, testId }) {
  return (
    <div className="emerge-card flex flex-col items-center justify-center px-6 py-14 text-center" data-testid={testId}>
      {Icon && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
          <Icon className="h-6 w-6 text-muted-foreground" />
        </div>
      )}
      <p className="text-lg font-semibold text-foreground">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Btn({ variant = "primary", size = "md", className, children, ...props }) {
  const base = "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] font-semibold disabled:opacity-60 disabled:pointer-events-none [&_svg]:shrink-0";
  const sizes = { sm: "h-9 px-3 text-sm [&_svg]:h-4 [&_svg]:w-4", md: "h-10 px-4 text-sm [&_svg]:h-4 [&_svg]:w-4", lg: "h-12 px-5 text-base [&_svg]:h-5 [&_svg]:w-5", icon: "h-9 w-9 [&_svg]:h-4 [&_svg]:w-4" };
  const variants = {
    primary: "bg-primary text-white hover:bg-[hsl(var(--app-navy-deep))]",
    secondary: "border border-border bg-white text-foreground hover:bg-secondary",
    ghost: "text-foreground hover:bg-secondary",
    danger: "bg-destructive text-white hover:bg-[hsl(8_55%_42%)]",
    dangerOutline: "border border-destructive/40 bg-white text-destructive hover:bg-destructive/5",
    teal: "bg-[hsl(var(--app-teal))] text-white hover:bg-[hsl(186_89%_24%)]",
  };
  return (
    <button type="button" className={cn(base, sizes[size], variants[variant], className)} style={{ transition: "background-color 160ms, color 160ms, border-color 160ms" }} {...props}>
      {children}
    </button>
  );
}

export function Card({ className, children, ...props }) {
  return (
    <div className={cn("emerge-card", className)} {...props}>
      {children}
    </div>
  );
}

export function FieldRow({ label, children, className }) {
  return (
    <div className={cn("grid grid-cols-[130px_1fr] gap-3 py-2.5 text-sm", className)}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground break-words">{children ?? "-"}</dd>
    </div>
  );
}

export function Pagination({ page, pageSize, total, onChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm text-muted-foreground">
      <span>
        Page {page} of {pages} - {total} results
      </span>
      <div className="flex gap-2">
        <Btn variant="secondary" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="pagination-prev">
          Previous
        </Btn>
        <Btn variant="secondary" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="pagination-next">
          Next
        </Btn>
      </div>
    </div>
  );
}

export function LabeledSelect({ label, id, children, className, ...props }) {
  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </label>
      )}
      <select id={id} className="emerge-input !h-10 appearance-none bg-white pe-8 text-sm" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%2363757a' stroke-width='2'><path d='M4 6l4 4 4-4'/></svg>\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 0.6rem center" }} {...props}>
        {children}
      </select>
    </div>
  );
}
